import { z } from 'zod';
import { coreIdSchema, EntityIdSchema } from './ids.js';
import { RoleIdSchema, type RoleId } from './organization.types.js';
import { PaginationMetaSchema, paginationParamsSchema } from './pagination.types.js';

/**
 * A resource grant shares ONE resource with a subject for an interval.
 * Cross-owner access (for example, another user's agent session) is only
 * ever conferred by one of these rows — never by an org-wide role and
 * never by the platform-admin tier. `expiresAt` is the interval's
 * exclusive end; `null` means the grant never expires on its own.
 * Revocation pins `expiresAt` to the revocation instant and keeps the
 * row as the audit record of the share.
 */

/**
 * Who a grant is addressed to: one user, every member of a team, or
 * every member of the organization the grant lives in.
 */
export const ResourceGrantSubjectTypeSchema = z
  .enum(['user', 'team', 'org'])
  .meta({ id: 'ResourceGrantSubjectType' });
export type ResourceGrantSubjectType = z.infer<typeof ResourceGrantSubjectTypeSchema>;

/**
 * The id a grant's subject can be: a user, team, or organization id —
 * the three kinds {@link ResourceGrantSubjectTypeSchema} names, and
 * nothing else. Which of the three is legal is pinned by `subjectType`;
 * the service validates the pairing (and org/team membership) on create.
 */
export const ResourceGrantSubjectIdSchema = z.union([
  coreIdSchema('user'),
  coreIdSchema('team'),
  coreIdSchema('organization'),
]);
export type ResourceGrantSubjectId = z.infer<typeof ResourceGrantSubjectIdSchema>;

/**
 * The two sharing access levels the UI offers: `view` confers the shared
 * kind's read slug, `edit` its read AND mutation-tier slug — on the ONE
 * granted resource only.
 */
export const RESOURCE_GRANT_ACCESSES = ['view', 'edit'] as const;
export type ResourceGrantAccess = (typeof RESOURCE_GRANT_ACCESSES)[number];

/**
 * Id prefix of the per-kind sharing roles. They are built-in roles like
 * any other (`builtin-<scope>-<slug>`, scope `org`), but their slug is
 * reserved: the server mints exactly one pair per declared resource kind
 * and binds it to that kind's own permission slugs
 * (`syncResourceGrantRoles` in `server/src/lib/resource-grant-roles.ts`),
 * so they never appear in org role pickers or member assignments.
 */
const RESOURCE_GRANT_ROLE_PREFIX = 'builtin-org-grant-';

/**
 * The role a share of `resourceType` at `access` confers.
 *
 * Sharing used to ride the built-in org Viewer / Member roles, which
 * confer whatever the *organization* decided those tiers mean. That was
 * wrong in both directions: an `edit` share handed over the whole member
 * permission set on the resource, while kinds whose write slug the member
 * role deliberately withholds (`personas:write`, `tailscale:manage`, …)
 * got an "edit" share that could not edit. A share confers the shared
 * kind's slugs and nothing else, so the role is per kind and per tier.
 */
export function resourceGrantRoleId(resourceType: string, access: ResourceGrantAccess): RoleId {
  return `${RESOURCE_GRANT_ROLE_PREFIX}${resourceType}-${access}` as RoleId;
}

/** The `roles.slug` half of {@link resourceGrantRoleId}. */
export function resourceGrantRoleSlug(resourceType: string, access: ResourceGrantAccess): string {
  return `grant-${resourceType}-${access}`;
}

/**
 * The kind and tier a sharing role id names, or `undefined` for any other
 * role. The tier is the id's last segment and kind names are kebab-case,
 * so the split is unambiguous.
 */
export function parseResourceGrantRoleId(
  roleId: string,
): { resourceType: string; access: ResourceGrantAccess } | undefined {
  if (!roleId.startsWith(RESOURCE_GRANT_ROLE_PREFIX)) return undefined;
  const rest = roleId.slice(RESOURCE_GRANT_ROLE_PREFIX.length);
  const separator = rest.lastIndexOf('-');
  if (separator <= 0) return undefined;
  const access = rest.slice(separator + 1);
  if (access !== 'view' && access !== 'edit') return undefined;
  return { resourceType: rest.slice(0, separator), access };
}

/** Whether `roleId` is one of the reserved per-kind sharing roles. */
export function isResourceGrantRoleId(roleId: string): boolean {
  return parseResourceGrantRoleId(roleId) !== undefined;
}

/**
 * The org roles shares rode before the per-kind roles above. Grants
 * created against a kind the registry declares are migrated onto the new
 * roles at boot; rows of undeclared types keep these, so read paths (the
 * access badge, `resourceGrantAccessOf`) still have to recognize them.
 */
export const LEGACY_RESOURCE_GRANT_ACCESS_ROLES = {
  view: 'builtin-org-viewer',
  edit: 'builtin-org-member',
} as const;

/**
 * The sharing tier a grant's role represents — for display. `undefined`
 * for a role that is neither a per-kind sharing role nor one of the
 * legacy pair (an admin may confer any role the org has).
 */
export function resourceGrantAccessOf(roleId: string): ResourceGrantAccess | undefined {
  const parsed = parseResourceGrantRoleId(roleId);
  if (parsed) return parsed.access;
  if (roleId === LEGACY_RESOURCE_GRANT_ACCESS_ROLES.view) return 'view';
  if (roleId === LEGACY_RESOURCE_GRANT_ACCESS_ROLES.edit) return 'edit';
  return undefined;
}

/**
 * Who opened a grant: a person acting through a share surface (`member`,
 * the default and what every grant was before this existed), or the host
 * applying a share default when a session was created — the org's
 * (`org-default`), or the launching member's own (`user-default`).
 *
 * The distinction is for reading, not for authorization — every source
 * confers access identically and is revocable the same way. It exists
 * because a default that opens one grant per session would otherwise bury
 * the deliberate shares on the org security Sharing page under a row for
 * every session ever launched. Tagging them also keeps a retention pass
 * over auto-applied rows available later without having to guess which
 * ones they were.
 *
 * `user-default` has no writer yet: per-member share defaults are a
 * follow-up, and the value is declared here (and in the column's check
 * constraint) up front so that change is a new resolver layer rather than
 * a migration against a shipped constraint. It already falls on the right
 * side of every existing reader — the Sharing page filters `['member']`,
 * meaning "shares a person opened deliberately", which no default is.
 */
export const RESOURCE_GRANT_SOURCES = ['member', 'org-default', 'user-default'] as const;
export const ResourceGrantSourceSchema = z
  .enum(RESOURCE_GRANT_SOURCES)
  .meta({ id: 'ResourceGrantSource' });
export type ResourceGrantSource = z.infer<typeof ResourceGrantSourceSchema>;

/** What a grant with no recorded source is: every row predating the column. */
export const DEFAULT_RESOURCE_GRANT_SOURCE: ResourceGrantSource = 'member';

export const ResourceGrantSchema = z
  .object({
    id: coreIdSchema('resourceGrant'),
    organizationId: coreIdSchema('organization'),
    resourceType: z.string(),
    resourceId: EntityIdSchema,
    subjectType: ResourceGrantSubjectTypeSchema,
    subjectId: ResourceGrantSubjectIdSchema,
    roleId: RoleIdSchema,
    createdAt: z.number(),
    /** Exclusive end of the sharing interval (epoch ms); `null` never expires. */
    expiresAt: z.number().nullable(),
    /**
     * Whether the CALLER may manage this grant (change its expiry, revoke
     * it): they own the shared resource or hold the grant-admin tier.
     * Computed per caller by the routes — a user addressed by a personal
     * grant may additionally decline it (revoke only), which the client
     * derives from `subjectType`/`subjectId` and its own user id.
     */
    canManage: z.boolean().optional(),
    /**
     * How the grant came to exist. Optional on the wire so payloads and
     * fixtures predating the field still validate; absent reads `member`.
     */
    source: ResourceGrantSourceSchema.optional(),
  })
  .meta({ id: 'ResourceGrant' });
export type ResourceGrant = z.infer<typeof ResourceGrantSchema>;

export const CreateResourceGrantRequestSchema = z
  .object({
    resourceType: z.string().min(1).max(64),
    resourceId: EntityIdSchema,
    subjectType: ResourceGrantSubjectTypeSchema,
    /**
     * The user, team, or organization id the share addresses. For
     * `subjectType: 'org'` it must be the active organization's id.
     */
    subjectId: ResourceGrantSubjectIdSchema,
    roleId: RoleIdSchema,
    /**
     * Exclusive end of the sharing interval (epoch ms). Omitted or
     * `null`: the grant never expires on its own and stays open until it
     * is revoked. A finite value must be in the future.
     */
    expiresAt: z.number().nullable().optional(),
  })
  .meta({ id: 'CreateResourceGrantRequest' });
export type CreateResourceGrantRequest = z.infer<typeof CreateResourceGrantRequestSchema>;

export const SetResourceGrantExpiryRequestSchema = z
  .object({
    /**
     * New exclusive end of the sharing interval (epoch ms), or `null`
     * for a grant that never expires. A finite value must be in the
     * future — ending a grant now is the revoke endpoint's job.
     */
    expiresAt: z.number().nullable(),
  })
  .meta({ id: 'SetResourceGrantExpiryRequest' });
export type SetResourceGrantExpiryRequest = z.infer<typeof SetResourceGrantExpiryRequestSchema>;

/**
 * Page size for the grant list, both the default and the ceiling.
 *
 * Deliberately far larger than the admin-table default (20): a page of
 * grants is not a screenful, it is the whole sharing surface the client
 * groups by resource, and every extra page is another round trip before
 * the page can render. 500 is what an org's live sharing looks like at the
 * high end, so the common case is one request while the read stays bounded
 * — grants accumulate for the life of an org, and `includeEnded` asks for
 * all of them.
 */
export const RESOURCE_GRANT_PAGE_SIZE = 500;

export const ListResourceGrantsQuerySchema = z.object({
  resourceType: z.string().optional(),
  resourceId: EntityIdSchema.optional(),
  subjectType: ResourceGrantSubjectTypeSchema.optional(),
  subjectId: ResourceGrantSubjectIdSchema.optional(),
  /** Include ended (revoked or lapsed) intervals. Default: active only. */
  includeEnded: z.enum(['true', 'false']).optional(),
  /**
   * Narrow the read to how each grant was opened. Omitted, every source is
   * included; `member` is what a page auditing deliberate shares wants,
   * because an org that shares new sessions by default accumulates one
   * `org-default` grant per session ever created.
   */
  source: ResourceGrantSourceSchema.optional(),
  ...paginationParamsSchema({
    defaultPageSize: RESOURCE_GRANT_PAGE_SIZE,
    maxPageSize: RESOURCE_GRANT_PAGE_SIZE,
  }).shape,
});
export type ListResourceGrantsQuery = z.infer<typeof ListResourceGrantsQuerySchema>;

export const ResourceGrantParamsSchema = z.object({ grantId: coreIdSchema('resourceGrant') });
export type ResourceGrantParams = z.infer<typeof ResourceGrantParamsSchema>;

export const ResourceGrantResponseSchema = z
  .object({
    grant: ResourceGrantSchema,
    /** On create: whether a new interval was opened (`false` = an active identical grant already existed). */
    created: z.boolean().optional(),
  })
  .meta({ id: 'ResourceGrantResponse' });
export type ResourceGrantResponse = z.infer<typeof ResourceGrantResponseSchema>;

/**
 * One page of grants plus the standard pagination metadata. The item key
 * is `grants` rather than the admin envelope's `items` because the shape
 * predates pagination and the clients read it by name; the metadata
 * fields are the shared ones, so a pager reads the same here as anywhere.
 * `total` counts the grants the CALLER can see, not the org's rows.
 */
export const ResourceGrantListResponseSchema = z
  .object({ grants: z.array(ResourceGrantSchema), ...PaginationMetaSchema.shape })
  .meta({ id: 'ResourceGrantListResponse' });
export type ResourceGrantListResponse = z.infer<typeof ResourceGrantListResponseSchema>;
